import { useContext } from 'react';
import { useCookies } from 'react-cookie';

import Logger from '@/core/utils/logging';

import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

export interface ExternalData {
    contactData?: Record<string, string | number | boolean>;
    SessionID?: string;
    gclid?: string;
    hutk?: string;
    reservationSource?: string;
    reservationComment?: string;
    persona?: string;
    selectedCustomerType?: string;
    variant?: string;
    userUuid?: string;
    configUuid?: string;
    unitTypeRegex?: string;
    unitTypeNames?: string[];
}

/**
 * Tracking identifiers set by external scripts (Google Ads auto-tagging, HubSpot, …) and
 * surfaced verbatim on the external data object. Each entry maps a browser cookie name to the
 * key it is exposed under on ExternalData. The app only ever reads these cookies; the external
 * scripts are responsible for writing them.
 *
 * To add a new tracking cookie: append one entry here and declare its key on ExternalData.
 */
export const TRACKING_COOKIES = [
    { cookie: 'gclid', key: 'gclid' },
    { cookie: 'hubspotutk', key: 'hutk' },
] as const satisfies readonly { cookie: string; key: keyof ExternalData }[];

/**
 * This is a hook because it uses context and cookies. The alternative is passing params but
 * that just means everywhere that needs to generate externalData needs to fetch and parse the params.
 *
 * Ideally in the future this is just a useReservation hook that handles all reservation related logic.
 * But that's a significant refactor and I'm not sure if it's worth it currently.
 */
export const useExternalData = () => {
    /** Gets the currently selected persona from the journey context */
    const { persona } = useContext(JourneyContext);
    /** Fetch all cookies */
    const [cookies] = useCookies([
        'contactData',
        'sessionId',
        'selectedCustomerType',
        ...TRACKING_COOKIES.map(({ cookie }) => cookie),
    ]);

    /**
   * Generates the external data object for the reservation request.
   *
   * @returns {ExternalData | undefined} External data object, or undefined if empty
   */
    const generateExternalData = (): ExternalData | undefined => {
        const { contactData } = cookies;
        const { sessionId } = cookies;
        const { selectedCustomerType } = cookies;
        const agentDetails = window.rapidstor_settings?.app_config?.journey?.agentDetails;
        const {
            variant,
            unitTypeRegex,
            unitTypeNames,
        } = window.rapidstor_settings?.rapidstor_options ?? {};
        const userUuid = localStorage.getItem('rapidstor-user-uuid');
        const configUuid = localStorage.getItem('rapidstor-config-uuid');

        const externalData: ExternalData = {};

        if (contactData) externalData.contactData = contactData;
        if (sessionId) externalData.SessionID = sessionId;
        TRACKING_COOKIES.forEach(({ cookie, key }) => {
            if (cookies[cookie]) externalData[key] = cookies[cookie];
        });
        if (agentDetails) {
            /** This data is set by another application, so lets not assume they're always set correctly */
            externalData.reservationSource = agentDetails.agentName ?? undefined;
            externalData.reservationComment = agentDetails.agentComment ?? undefined;
        }
        if (persona) externalData.persona = persona;
        if (selectedCustomerType) externalData.selectedCustomerType = selectedCustomerType;
        if (variant) externalData.variant = variant;
        if (userUuid) externalData.userUuid = userUuid;
        if (configUuid) externalData.configUuid = configUuid;
        if (unitTypeRegex) externalData.unitTypeRegex = unitTypeRegex;
        if (unitTypeNames) externalData.unitTypeNames = unitTypeNames;

        Logger.info(
            `[useExternalData] Generated external data: ${JSON.stringify(externalData)}`,
        );

        if (Object.values(externalData).every((value) => value === undefined)) {
            return undefined;
        }

        return externalData;
    };

    return {
        generateExternalData,
    };
};
